<template>
  <Field
    v-slot="{ field, errorMessage }"
    v-model="textAreaValue"
    :name="name"
    :rules="rule"
  >
    <div
      :ref="id"
      :class="`textarea-container${size === 'small' ? ' textarea-container-sm' : ''}`"
    >
      <textarea
        v-bind="{ ...$attrs, ...field }"
        :id="id"
        :type="type"
        placeholder=" "
        :maxlength="maximumLength"
        :aria-describedby="hideErrorMessage ? hideErrorMessageId : ariaErrorId"
        :aria-invalid="errorMessage"
        :class="{ 'is-invalid-input': errorMessage && delayErrorMsg || delayErrorMsgFun(errorMessage)}"
        :disabled="getDisableState"
        @input="onInput()"
        @keypress.enter="$emit('onEnter',textAreaValue)"
      />
      <label
        :class="`textarea-label-top${size === 'small' ? ' textarea-label-top-sm' : ''}`"
        :for="id"
      >
        {{ label }}
      </label>
    </div>
    <div
      v-show="errorMessage && hideErrorMessage && delayErrorMsg"
      :id="hideErrorMessageId"
      class="show-for-sr"
      aria-live="polite"
      aria-atomic="true"
    >
      <span> {{ errorMessage? `${'Error ' + errorMessage}` : '' }} </span>
    </div>
    <div
      v-show="errorMessage && !hideErrorMessage && delayErrorMsg"
      :id="ariaErrorId"
      aria-live="polite"
      aria-atomic="true"
      :class="['form-error', {'is-visible': errorMessage && !hideErrorMessage && delayErrorMsg}]"
    >
      <Icon
        class="error-red"
        role="graphics-symbol"
        :icon="csgErrorRed"
        :aria-label="errorMessage ? 'Error':''"
        :aria-hidden="!errorMessage && hideErrorMessage && delayErrorMsg"
      />
      <span> {{ errorMessage }} </span>
    </div>
  </Field>
</template>

<script setup>
import { ref, computed } from 'vue';
import { veevalidate } from '@vue-util/forms';
import { csgErrorRed } from '@csgSvgIcons/icons';
import Icon from '../icon';
import rules from '../inputText/rules.json';
import generateRandomId from '../../utils/randomNumber';

const { Field } = veevalidate;

const props = defineProps({
  id: {
    type: [String, Number],
    default: () => generateRandomId(),
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  rule: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String,
    default: '',
  },
  text: {
    type: String,
    default: '',
  },
  hideErrorMessage: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'large',
  },
  autocomplete: {
    type: String,
    default: 'on',
  },
});

const emit = defineEmits(['update:modelValue', 'onEnter']);

const textAreaValue = ref(props.modelValue);
const delayErrorMsg = ref(true);
const ariaErrorId = ref(generateRandomId());
const hideErrorMessageId = ref(generateRandomId());
const getDisableState = computed(() => props.disabled);
const rulesData = rules;

const maximumLength = computed(() => {
  if (props.rule.length && rulesData[props.rule]) {
    return rulesData[props.rule].maxlength;
  }
  return '1000';
});

function delayErrorMsgFun(shouldDelay) {
  if (shouldDelay) {
    setTimeout(() => {
      delayErrorMsg.value = true;
    }, 100);
  }
  delayErrorMsg.value = false;
}

function onInput() {
  emit('update:modelValue', textAreaValue.value);
}
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-forms;
</style>
